/**
 * lessonService — Firestore reads/writes for the `lessons` collection.
 * Centralizes the inline calls previously in Today and the dashboard's
 * Create-Test-Lesson action. Behavior is unchanged; callers build the document
 * shape (including any serverTimestamp fields).
 */

import { db } from '../firebase';
import {
	collection,
	doc,
	addDoc,
	getDocs,
	query,
	where,
	orderBy,
	limit,
	runTransaction,
	serverTimestamp,
} from 'firebase/firestore';

const COLLECTION = 'lessons';

// ── Session records for scheduled occurrences ──────────────────────────────
//
// THE GAP THIS CLOSES (card RC21GWvYXflM556QGUpT): the fall schedule lives in
// `timeSlots`, but the Lesson Dashboard opens a `lessons` doc — and nothing
// created one. From Aug 31 every real lesson would have had no dashboard.
//
// A `lessons` doc is NOT a duplicate of the schedule. It is the mutable record
// of a SESSION — startedAt / completedAt / status / carryForwardTuckedAt, plus
// notes and the song board. A timeSlots occurrence is a projection of a
// recurring template and has nowhere to put "started at 4:32pm on Sept 14", so
// the collection is the right shape; it was simply never populated.
//
// CREATED ON DEMAND (Anthony, 2026-08-12), not by a pre-creation job: no rows
// for lessons that never happen, and nothing to reconcile when a lesson is
// cancelled or moved.

// The id IS the occurrence identity, and that is the load-bearing choice here.
//
// Keyed on (slotId, ORIGIN date) — the exception map's key, not the displayed
// date — because a rescheduled lesson keeps its origin while its displayed date
// moves. Keying on the displayed date would mint a SECOND record every time a
// lesson is moved, splitting one session's notes across two docs.
//
// Deterministic, so it is idempotent by construction: a double-click, two
// teachers, or a later attendance pass all address the same document instead of
// racing to create duplicates. That last one matters beyond today — attendance
// is a real future feature, and this is what lets it mark a no-show on the
// exact record for a scheduled occurrence without inventing a parallel row.
export function lessonIdForOccurrence({ slotId, originDate }) {
	if (!slotId || !originDate) return null;
	return `occ_${slotId}_${originDate}`;
}

/**
 * The schedule fields an existing, NOT-YET-STARTED record should take from the
 * current occurrence, or null when nothing differs or the session has begun.
 *
 * THE BUG THIS CLOSES (2026-10-11): the record copies date/time/duration once,
 * at first open, and the timer counts down from those copies. A calendar edit
 * made after that (CalendarTab writes the slot's `exceptions`, never the
 * record) left Today showing the new time and the dashboard's timer and
 * stopwatch running against the old one. Until a session starts, the record's
 * schedule fields are a cache of the slot, so they follow it; once started or
 * completed they are history and never move.
 *
 * Only fields the caller actually supplied are compared, so a caller passing a
 * partial occurrence cannot blank a stored value.
 */
export function scheduleRefreshFor(existing, occurrence) {
	if (!existing || !occurrence) return null;
	if (existing.startedAt || existing.completedAt) return null;
	if (existing.status && existing.status !== 'scheduled') return null;
	const patch = {};
	if (occurrence.date && occurrence.date !== existing.date) patch.date = occurrence.date;
	if (occurrence.startTime && occurrence.startTime !== existing.time) patch.time = occurrence.startTime;
	if (occurrence.duration && occurrence.duration !== existing.duration) patch.duration = occurrence.duration;
	return Object.keys(patch).length ? patch : null;
}

/**
 * The session record for a scheduled occurrence, created if it does not exist.
 *
 * Returns the doc id. Transactional so a concurrent open cannot clobber a
 * session already in progress: an existing doc is never merged over (a second
 * click must not reset `startedAt`). The one thing it may receive is the
 * schedule refresh above, and only while the session has not started.
 */
export async function ensureLessonForOccurrence(occurrence) {
	const { slotId, originDate } = occurrence || {};
	const id = lessonIdForOccurrence({ slotId, originDate });
	if (!id) throw new Error('ensureLessonForOccurrence needs slotId and originDate');

	const ref = doc(db, COLLECTION, id);
	await runTransaction(db, async (tx) => {
		const snap = await tx.get(ref);
		if (snap.exists()) {
			const patch = scheduleRefreshFor(snap.data(), occurrence);
			if (patch) tx.update(ref, patch);
			return;
		}
		tx.set(ref, {
			// Schedule facts, copied so the record still reads correctly if the
			// slot is later edited or deleted — a session is a historical fact.
			date: occurrence.date,
			time: occurrence.startTime,
			duration: occurrence.duration || 45,
			studentId: occurrence.studentId || null,
			studentName: occurrence.studentName || '',
			instrument: occurrence.instrument || null,
			location: occurrence.location || null,
			lessonType: occurrence.isMakeup ? 'Make-up' : 'Private',
			// Provenance: what scheduled occurrence this session came from. This is
			// the join key an attendance view would use to line session records up
			// against the schedule.
			origin: { source: 'timeSlots', slotId, originDate },
			status: 'scheduled',
			createdAt: serverTimestamp(),
		});
	});
	return id;
}

// Lessons whose `date` falls in [startDateStr, endDateStr), ordered by date+time
// (the Today query). Dates are 'YYYY-MM-DD' strings, matching the stored field.
export async function fetchLessonsForDateRange(startDateStr, endDateStr) {
	const q = query(
		collection(db, COLLECTION),
		where('date', '>=', startDateStr),
		where('date', '<', endDateStr),
		orderBy('date'),
		orderBy('time')
	);
	const snapshot = await getDocs(q);
	return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createLesson(data) {
	return addDoc(collection(db, COLLECTION), data);
}

/**
 * The studio's test student — the first `students` doc flagged
 * `isTestStudent: true` (Tommy Test in prod, Anthony's QA family). Null when
 * none exists, so a caller can still create a student-less record.
 */
export async function findTestStudent() {
	const snap = await getDocs(query(collection(db, 'students'), where('isTestStudent', '==', true), limit(1)));
	if (snap.empty) return null;
	const d = snap.docs[0];
	return { id: d.id, ...d.data() };
}

/**
 * A test lesson for RIGHT NOW, for the Work home's admin-only "Create test
 * lesson" button (2026-09-04: it used to create a student-less record, which
 * opened the dashboard fine but could not exercise anything keyed on a
 * student — the warmup routine above all, since a routine is per student).
 *
 * Attached to the test student when one exists. `isTest: true` keeps it out
 * of the intake/attendance readers that filter on it, and with no `userId` a
 * family never sees it. Returns the created doc ref.
 */
export async function createTestLesson({ uid = null, now = new Date() } = {}) {
	const student = await findTestStudent();
	const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
	const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
	return createLesson({
		studentName: student ? student.fullName || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Test Student' : 'Test Student',
		studentId: student ? student.id : null,
		date: dateStr,
		time: timeStr,
		duration: 45,
		lessonType: 'Private',
		instrument: 'Voice',
		status: 'scheduled',
		createdAt: serverTimestamp(),
		createdBy: uid,
		isTest: true,
	});
}
